import { proccessNotes } from './components/JoinedForm/Helpers'

export const templateFormToProductKeys = new Set(['height', 'length', 'width', 'weight', 'shippingWeight', 'tags'])

export const numericFields = new Set(['height', 'length', 'width', 'weight', 'shippingWeight'])

// Only match the first set of numbers, remove everything else
// Make sure the value is positive
export const removeNonNumericCharacters = str => {
  if (typeof str !== 'string') return str

  // eslint-disable-next-line no-useless-escape
  let replacedStr = str.replace(/([^\.0-9])(.*)/g, '')

  replacedStr = parseFloat(replacedStr) >= 0 ? parseFloat(replacedStr).toString() : undefined
  return replacedStr
}

const mergeTemplateValuesIntoProduct = (product, productAttrsFromTemplate) => {
  const mergedProduct = { ...product }
  for (const key in productAttrsFromTemplate) {
    if (productAttrsFromTemplate[key] || productAttrsFromTemplate[key] === 0) {
      let newValue = productAttrsFromTemplate[key]

      if (numericFields.has(key)) {
        newValue = removeNonNumericCharacters(newValue)
      }

      // Don't clobber existing tags for draft products
      if (key === 'tags' && mergedProduct.tags?.length > 0) {
        newValue = [...product.tags, ...newValue]
      }

      // Automatically populate the shippingWeight field when the weight field is set
      if (key === 'weight' && !Object.prototype.hasOwnProperty.call(productAttrsFromTemplate, 'shippingWeight')) {
        mergedProduct.shippingWeight = newValue
      }

      mergedProduct[key] = newValue
    }
  }

  return mergedProduct
}

const serializeTag = field => `${field.variableName}_${field.props.value}`

export const getProductAttrsFromTemplateForm = (form: any) => {
  const productAttributesFromForm: any = {}
  for (const key in form) {
    if (form[key]) {
      const field = form[key]
      const fieldValue = field.props.value
      const productKey = form[key].productField
      if (templateFormToProductKeys.has(productKey) && field.props) {
        if (productKey === 'tags') {
          productAttributesFromForm.tags = productAttributesFromForm.tags || []
          if (fieldValue) productAttributesFromForm.tags.push(serializeTag(field))
        } else {
          productAttributesFromForm[productKey] = fieldValue
        }
      }
    }
  }
  return productAttributesFromForm
}

export const templateProductFieldMerge = (form, product) => {
  if (!form) return product

  const productAttrsFromTemplate = getProductAttrsFromTemplateForm(form)

  return mergeTemplateValuesIntoProduct(product, productAttrsFromTemplate)
}

export interface ProductForTemplateUtils extends Record<string, unknown> {
  length?: number | null
  width?: number | null
  height?: number | null
  weight?: number | null
  shippingWeight?: string | null
  tags?: string[]
}

const OPTION_FIELD_TYPES = new Set(['select', 'radio'])

const optionListIncludesValue = (options: { key?: string; value?: string }[], value: unknown) => {
  if (value === null || value === undefined || value === '') return false

  const normalized = value.toString().toLowerCase()
  return (options || []).some(
    option => option?.key?.toLowerCase() === normalized || option?.value?.toLowerCase() === normalized
  )
}

const canCopyTemplateFieldValue = (previousField: any, newField: any) => {
  if (previousField.type === 'heading' || newField.type === 'heading') return false
  if (previousField.type !== newField.type) return false
  if (previousField.variableName !== newField.variableName) return false

  if (OPTION_FIELD_TYPES.has(newField.type)) {
    return optionListIncludesValue(newField.props?.options, previousField.props?.value)
  }

  return true
}

const mergeComboboxTemplateField = (newField: any, previousField: any) => {
  const { value } = previousField.props
  const options = newField.props?.options || []

  if (value === null || value === undefined || value === '') {
    return { ...newField, props: { ...newField.props, value } }
  }

  const existingOption = options.some(option => option.key === value || option.value === value)
  if (existingOption) {
    return { ...newField, props: { ...newField.props, value } }
  }

  return {
    ...newField,
    props: {
      ...newField.props,
      options: [...options, { value, key: value }],
      value
    }
  }
}

const mergeTemplateFieldValue = (newField: any, previousField: any) => {
  if (newField.type === 'combobox') {
    return mergeComboboxTemplateField(newField, previousField)
  }

  const { value } = previousField.props

  if (newField.type === 'checkbox') {
    const checked = !!value
    return { ...newField, props: { ...newField.props, value: checked, checked } }
  }

  return { ...newField, props: { ...newField.props, value } }
}

// Retain user-entered builder template values when Express List reloads category configuration.
export const mergePreviousTemplateFormValues = (previousForm: any[], template: any) => {
  if (!template?.form || !previousForm?.length) return template

  const previousByVariableName = new Map<string, any>()
  previousForm.forEach(field => {
    if (field?.variableName && !previousByVariableName.has(field.variableName)) {
      previousByVariableName.set(field.variableName, field)
    }
  })

  const newForm = template.form.map((field: any) => {
    const previousField = previousByVariableName.get(field.variableName)
    if (!previousField || !canCopyTemplateFieldValue(previousField, field)) {
      return field
    }

    return mergeTemplateFieldValue(field, previousField)
  })

  return { ...template, form: newForm }
}

export const setDraftProductValuesInTemplateForm = (product: ProductForTemplateUtils, template) => {
  if (!template) return template
  if (!template.form) return template

  const newForm = template.form.map((field: any) => {
    const productKey = field.productField

    if (
      templateFormToProductKeys.has(productKey) &&
      field.props &&
      product[productKey] &&
      productKey !== 'tags' // don't set tags as default template value because multiple template fields can map to it
    ) {
      // toString() avoids cases like product height, where it might begin as a number, then change into a string once the user touches the field.
      return { ...field, props: { ...field.props, value: product[productKey]?.toString() } }
    }
    return field
  })
  return { ...template, form: newForm }
}
const hammoqValueToBool = str => str === 'yes' || str === 'Yes' || str === 'YES'

export const setNotesValuesInTemplateForm = (notes, template) => {
  if (!template || !notes || notes.length === 0) return template
  const attributes = proccessNotes(notes, true)
  if (Object.keys(attributes).length < 1) return template

  const newForm = template.form.map((input: any) => {
    const inputType = input.type
    const propsEle = input.props
    const { variableName } = input
    const labelName = propsEle.label

    let autofillValue = null
    // this works to correclty insert the value for Multiple Choice fields, but the UI element doesn't appear correctly checked, so we will not support it
    // if (inputType === 'checkboxGroup') {
    //   propsEle.options.forEach(optionValue => {
    //     if (
    //       optionValue.key.toLowerCase() == attributes[variableName.toLowerCase()]?.toLowerCase() ||
    //       optionValue.key.toLowerCase() == attributes[labelName.toLowerCase()]?.toLowerCase()
    //     ) {
    //       autofillValue = [optionValue.key]
    //     }
    //   })
    // } else
    if (inputType === 'checkbox') {
      if (attributes[variableName.toLowerCase()] || attributes[labelName.toLowerCase()])
        autofillValue = hammoqValueToBool(attributes[variableName.toLowerCase()] || attributes[labelName.toLowerCase()])
    } else if (inputType === 'combobox' || inputType === 'select' || inputType === 'radio') {
      if (attributes[variableName.toLowerCase()] || attributes[labelName.toLowerCase()]) {
        propsEle?.options?.forEach((optionValue: any) => {
          if (
            optionValue?.key.toLowerCase() === attributes[variableName.toLowerCase()]?.toLowerCase() ||
            optionValue?.key.toLowerCase() === attributes[labelName.toLowerCase()]?.toLowerCase()
          ) {
            autofillValue = optionValue.key
          }
        })
      }
    }
    // ignore these two form types until the bug(https://app.asana.com/0/1204859485810598/1206288730596279/f) is resolved and we can get the block above commented out to work correctly,
    // heading will never be supported since it isn't an input
    else if (inputType !== 'checkboxGroup' || inputType !== 'heading') {
      autofillValue = attributes[variableName.toLowerCase()] || attributes[labelName.toLowerCase()]
    }

    if (autofillValue) {
      return { ...input, props: { ...input.props, value: autofillValue } }
    }
    return input
  })

  return { ...template, form: newForm }
}

export function clearUprightPerformanceMarks() {
  performance.getEntriesByType('mark').forEach(m => {
    if (m.name.includes('upright:')) {
      performance.clearMarks(m.name)
    }
  })
}

export function newVariantFromProduct(product, title) {
  return {
    id: null,
    title,
    sku: '',
    quantity: 1,
    inventoryLocationId: product.inventoryLocationId
  }
}

export function validVariantPricing(variant) {
  if (
    variant.compareAtPrice === null ||
    variant.compareAtPrice === undefined ||
    variant.compareAtPrice === '' ||
    +variant.compareAtPrice >= +variant.price
  ) {
    return true
  }

  return false
}
